import { FC, useRef, useEffect, useState, useCallback, FormEvent } from 'react'
import { CircularProgress } from '@mui/material'
import { ChatMessage, AgentStatus } from '../../utils/types'
import { CHAT_FEATURES, getStatusMessage } from '../../utils/config'
import { ChatMessageItem } from './components'
import {
  ChatAreaContainer,
  ErrorAlert,
  ErrorText,
  ContentArea,
  MessagesContainer,
  WelcomeContainer,
  WelcomeHeading,
  InputContainer,
  InputBox,
  InputBoxCompact,
  InputWrapper,
  ChatInput,
  InputActions,
  InputActionsCompact,
  SendButton,
  StopButton,
  SuggestionsContainer,
  SuggestionChip,
  SuggestionText,
  BottomInputContainer,
  StatusIndicator,
  StatusSpinner,
  StatusChip
} from './chatArea.styles'

const SendArrowIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10 16.25L10 3.75M10 3.75L4.375 9.375M10 3.75L15.625 9.375"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const StopIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M0.625 2.5C0.625 1.46447 1.46447 0.625 2.5 0.625H10C11.0355 0.625 11.875 1.46447 11.875 2.5V10C11.875 11.0355 11.0355 11.875 10 11.875H2.5C1.46447 11.875 0.625 11.0355 0.625 10V2.5Z"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const QUICK_SUGGESTIONS = [
  'Nexa, what can you help me with?',
  'How long does it take us to resolve an incident on average?',
  'How many new devices were added to my network in the past 3 months?'
]

interface ChatAreaProps {
  messages: ChatMessage[]
  agentStatus: AgentStatus | null
  isLoading: boolean
  isLoadingThread?: boolean
  isLoadingMore?: boolean
  hasMoreMessages?: boolean
  isAgentReady: boolean
  error: Error | null
  onSendMessage: (message: string) => void
  onStop: () => void
  onLoadMore?: () => void
}

const ChatArea: FC<ChatAreaProps> = ({
  messages,
  agentStatus,
  isLoading,
  isLoadingThread = false,
  isLoadingMore = false,
  hasMoreMessages = false,
  isAgentReady,
  error,
  onSendMessage,
  onStop,
  onLoadMore
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesStartRef = useRef<HTMLDivElement>(null)
  const contentAreaRef = useRef<HTMLDivElement>(null)
  const prevMessagesLengthRef = useRef<number>(0)
  const [input, setInput] = useState('')
  const hasMessages = messages.length > 0

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  // Handle scroll to detect when user scrolls to top
  const handleScroll = useCallback(() => {
    const contentArea = contentAreaRef.current
    if (!contentArea || isLoadingMore || !hasMoreMessages || !onLoadMore) return

    // Load more when scrolled near the top (within 100px)
    if (contentArea.scrollTop < 100) {
      onLoadMore()
    }
  }, [isLoadingMore, hasMoreMessages, onLoadMore])

  // Maintain scroll position when prepending older messages
  useEffect(() => {
    const contentArea = contentAreaRef.current
    if (!contentArea) return

    // If messages were added at the beginning (loading more), maintain scroll position
    if (messages.length > prevMessagesLengthRef.current && prevMessagesLengthRef.current > 0) {
      const addedCount = messages.length - prevMessagesLengthRef.current
      // If we loaded more (prepended), keep the view stable
      if (addedCount > 0 && contentArea.scrollTop < 200) {
        // Find the first previously visible message and scroll to it
        const firstPreviousMessage = contentArea.querySelector(`[data-message-index="${addedCount}"]`)
        if (firstPreviousMessage) {
          firstPreviousMessage.scrollIntoView({ behavior: 'auto', block: 'start' })
        }
      }
    }

    prevMessagesLengthRef.current = messages.length
  }, [messages.length])

  useEffect(() => {
    // Only auto-scroll to bottom for new messages at the end, not when loading older messages
    const contentArea = contentAreaRef.current
    if (!contentArea) {
      scrollToBottom()
      return
    }

    // Check if user is near the bottom (within 200px)
    const isNearBottom = contentArea.scrollHeight - contentArea.scrollTop - contentArea.clientHeight < 200

    if (isNearBottom) {
      scrollToBottom()
    }
  }, [messages])

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (input.trim() && !isLoading && isAgentReady) {
      onSendMessage(input)
      setInput('')
    }
  }

  const handleSuggestionClick = (suggestion: string) => {
    if (!isLoading && isAgentReady) {
      onSendMessage(suggestion)
    }
  }

  return (
    <ChatAreaContainer>
      {/* Error display */}
      {error && (
        <ErrorAlert>
          <ErrorText>Error: {error.message}</ErrorText>
        </ErrorAlert>
      )}

      {/* Main content area */}
      <ContentArea ref={contentAreaRef} hasMessages={hasMessages || isLoadingThread} onScroll={handleScroll}>
        {isLoadingThread ? (
          // Loading thread state
          <MessagesContainer>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress size={32} />
            </div>
          </MessagesContainer>
        ) : hasMessages ? (
          // Messages view
          <MessagesContainer>
            {/* Loading more indicator at top */}
            <div ref={messagesStartRef} />
            {isLoadingMore && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                <CircularProgress size={24} />
              </div>
            )}

            {messages.map((message, index) => (
              <div key={message.id} data-message-index={index}>
                <ChatMessageItem message={message} />
              </div>
            ))}

            {/* Agent status indicator */}
            {agentStatus && CHAT_FEATURES.showStatus && (
              <StatusIndicator>
                <StatusSpinner size={16} />
                <StatusChip label={getStatusMessage(agentStatus.status)} size="small" />
              </StatusIndicator>
            )}

            <div ref={messagesEndRef} />
          </MessagesContainer>
        ) : (
          // Empty state - Welcome view
          <WelcomeContainer>
            {/* Heading */}
            <WelcomeHeading>How can I help you?</WelcomeHeading>

            {/* Input and suggestions container */}
            <InputContainer>
              {/* Input Box */}
              <InputBox component="form" onSubmit={handleSubmit}>
                <InputWrapper>
                  <ChatInput
                    fullWidth
                    multiline
                    rows={3}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={isAgentReady ? 'Ask or search anything...' : 'Initializing agent...'}
                    disabled={isLoading || !isAgentReady}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        handleSubmit(e as unknown as FormEvent)
                      }
                    }}
                  />
                </InputWrapper>

                {/* Action buttons */}
                <InputActions>
                  {isLoading ? (
                    <StopButton onClick={onStop}>
                      <StopIcon />
                    </StopButton>
                  ) : (
                    <SendButton type="submit" disabled={!input.trim() || !isAgentReady}>
                      <SendArrowIcon />
                    </SendButton>
                  )}
                </InputActions>
              </InputBox>

              {/* Quick Suggestions */}
              <SuggestionsContainer>
                {QUICK_SUGGESTIONS.map((suggestion) => (
                  <SuggestionChip key={suggestion} onClick={() => handleSuggestionClick(suggestion)}>
                    <SuggestionText>{suggestion}</SuggestionText>
                  </SuggestionChip>
                ))}
              </SuggestionsContainer>
            </InputContainer>
          </WelcomeContainer>
        )}
      </ContentArea>

      {/* Input at bottom when there are messages or loading thread */}
      {(hasMessages || isLoadingThread) && (
        <BottomInputContainer>
          <InputBoxCompact component="form" onSubmit={handleSubmit}>
            <InputWrapper>
              <ChatInput
                fullWidth
                multiline
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={
                  isLoadingThread
                    ? 'Loading conversation...'
                    : isAgentReady
                    ? 'Ask or search anything...'
                    : 'Initializing agent...'
                }
                disabled={isLoading || isLoadingThread || !isAgentReady}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSubmit(e as unknown as FormEvent)
                  }
                }}
              />
            </InputWrapper>

            {/* Action buttons */}
            <InputActionsCompact>
              {isLoading ? (
                <StopButton onClick={onStop}>
                  <StopIcon />
                </StopButton>
              ) : (
                <SendButton type="submit" disabled={!input.trim() || isLoadingThread || !isAgentReady}>
                  <SendArrowIcon />
                </SendButton>
              )}
            </InputActionsCompact>
          </InputBoxCompact>
        </BottomInputContainer>
      )}
    </ChatAreaContainer>
  )
}

export default ChatArea
